// Uppdatering av ProcessNavigation-komponenten för att visa highlighting på aktiva element
import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface ProcessItem {
  title: string;
  number: string;
}

interface ProcessNavigationProps {
  processes: ProcessItem[];
  activeProcess: string;
  onSelect: (title: string) => void;
  type: "main" | "sub";
  highlightedItems?: Set<string>;
}

// Uppdaterad ProcessChevron-komponent med förbättrad border-rendering
const ProcessChevron = ({ 
  title, 
  number, 
  active, 
  onClick, 
  width, 
  type,
  fontSize,
  isMobile,
  isHighlighted
}: { 
  title: string; 
  number: string; 
  active: boolean; 
  onClick: () => void; 
  width: number;
  type: "main" | "sub";
  fontSize: string;
  isMobile: boolean;
  isHighlighted: boolean;
}) => {
  // Anpassa höjden baserat på enhet och typ
  const chevronHeight = isMobile 
    ? (type === "main" ? "h-12" : "h-10") 
    : (type === "main" ? "h-16" : "h-14");
  
  // Anpassa padding för texten baserat på enhet
  const textPadding = isMobile ? "pl-12 pr-4" : "pl-16 pr-6";
  
  // Anpassa positionering av numret baserat på enhet
  const numberPosition = isMobile ? "left-4" : "left-6";

  // Skapa ett säkert element-ID
  const safeTitle = title ? title.replace(/\s+/g, '-').toLowerCase() : 'unknown';

  // Förbättrad centrering av innehåll
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative cursor-pointer flex-shrink-0 ${
        chevronHeight
      } ${
        isHighlighted && active ? "highlighted-chevron-active" : 
        isHighlighted ? "highlighted-chevron" : ""
      } process-chevron`}
      style={{ 
        width: `${width}px`
      }}
      onClick={onClick}
      id={`process-chevron-${type}-${safeTitle}`}
      data-testid={`process-chevron-${type}`}
      data-active={active ? "true" : "false"}
      data-highlighted={isHighlighted ? "true" : "false"}
    >
      {/* Skugglager för bättre konturer - renderas först */}
      <svg 
        className="absolute left-0 top-0 w-full h-full chevron-path-shadow"
        viewBox={`0 0 ${width} ${isMobile ? 48 : 64}`} 
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        id={`chevron-shadow-${type}-${safeTitle}`}
      >
        <defs>
          <filter id="chevronShadow" x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0" dy="0" stdDeviation="1" floodColor="#000000" floodOpacity="0.3" />
          </filter>
        </defs>
        <path 
          d={`M0,0 H${width - (isMobile ? 15 : 20)} L${width},${isMobile ? 24 : 32} L${width - (isMobile ? 15 : 20)},${isMobile ? 48 : 64} H0 L${isMobile ? 15 : 20},${isMobile ? 24 : 32} L0,0 Z`} 
          fill="none"
          stroke="#0F172A"
          strokeWidth="0.5"
          filter="url(#chevronShadow)"
          strokeLinejoin="round"
          className="chevron-shadow-path"
        />
      </svg>
      
      {/* Huvudchevron med fyllnad */}
      <svg 
        className="absolute left-0 top-0 w-full h-full"
        viewBox={`0 0 ${width} ${isMobile ? 48 : 64}`} 
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        id={`chevron-main-${type}-${safeTitle}`}
      >
        <path 
          d={`M0,0 H${width - (isMobile ? 15 : 20)} L${width},${isMobile ? 24 : 32} L${width - (isMobile ? 15 : 20)},${isMobile ? 48 : 64} H0 L${isMobile ? 15 : 20},${isMobile ? 24 : 32} L0,0 Z`} 
          className={`chevron-main-path ${active ? "fill-orange-300" : isHighlighted ? "fill-[#FFF4E3]" : "fill-[#FFF4E3]"}`}
          stroke={
            (isHighlighted && active) ? "rgb(180, 237, 185)" :
            isHighlighted ? "rgb(180, 237, 185)" : 
            "#0F172A"
          }
          strokeWidth={
            (isHighlighted && active) ? "2" :
            isHighlighted ? "1.5" : 
            "1"
          }
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      
      {/* Content container som centrerar allt innehåll i pilen */}
      <div className="absolute inset-0 flex items-center justify-center px-6 chevron-content-container">
        <div className="flex items-center w-full justify-center gap-2 max-w-full">
          {/* Nummer-del med konsekvent formatering */}
          <div className="flex-shrink-0 min-w-[1.5rem] text-center font-mono text-xs font-medium chevron-text" id={`chevron-number-${type}-${safeTitle}`}>
            <span className={
              active ? (isHighlighted ? "text-gray-900 font-bold" : "text-gray-900") : 
              "text-[#CE5325]"
            }>
              {number}
            </span>
          </div>
          
          {/* Titel-del som håller sig nära numret */}
          <span 
            className={`
              font-medium truncate chevron-text
              ${active 
                ? (isHighlighted ? "text-gray-900 font-bold" : "text-gray-900") 
                : "text-gray-900"
              }
            `}
            style={{ fontSize }}
            id={`chevron-title-${type}-${safeTitle}`}
          >
            {title || ""}
          </span>
        </div>
      </div>
      
    </motion.div>
  );
};

// Kompakt visning för mobila enheter när många processer
const MobileProcessSelector = ({
  processes,
  activeProcess,
  onSelect,
  type,
  highlightedItems
}: ProcessNavigationProps) => {
  return (
    <div className="w-full p-2" id={`mobile-process-selector-${type}`}>
      <select
        value={activeProcess}
        onChange={(e) => onSelect(e.target.value)}
        className="w-full p-2 border border-gray-700 rounded-lg bg-gray-800 text-white"
        id={`mobile-process-select-${type}`}
        data-testid={`mobile-process-select-${type}`}
      >
        {processes.map((process) => {
          // Säker ID-generering
          const safeTitle = process.title ? process.title.replace(/\s+/g, '-').toLowerCase() : 'unknown';
          const isHighlighted = highlightedItems?.has(process.title);
          return (
            <option 
              key={process.title || `unknown-${Math.random()}`} 
              value={process.title}
              className={isHighlighted ? "font-bold text-[rgb(180,237,185)]" : ""}
              id={`mobile-option-${type}-${safeTitle}`}
              data-highlighted={isHighlighted ? "true" : "false"}
            >
              {process.number}. {process.title || ""} {isHighlighted ? "•" : ""}
            </option>
          );
        })}
      </select>
    </div>
  );
};

const ProcessNavigation: React.FC<ProcessNavigationProps> = ({
  processes,
  activeProcess,
  onSelect,
  type,
  highlightedItems = new Set(),
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({
    widths: [] as number[],
    fontSize: type === "main" ? "0.875rem" : "0.75rem", // Default font size
    isMobile: false,                      // Om vi är på en mobil enhet
    useCompactView: false,               // Om vi ska visa den kompakta vyn (select)
    visibleProcesses: processes          // Processer som visas i chevron-vyn
  });
  
  // Skapa en nyckel för att trigga omberäkning när processer ändras
  const processesKey = processes.map(p => p.title || 'unknown').join('|');
  
  useEffect(() => {
    const calculateLayout = () => {
      if (!containerRef.current) return;
      
      // Få tillgänglig bredd för hela containern
      const containerWidth = containerRef.current.clientWidth;
      
      // Bestäm om vi är på en mobil enhet
      const isMobile = window.innerWidth < 768; // md breakpoint i Tailwind
      
      // På mindre skärmar, visa endast aktiv process och två grannar om många processer
      let visibleProcesses = [...processes];
      let useCompactView = false;
      
      // Endast använd compact view på mobiler med många processer
      // Detta gör att vi återgår till normal vy när fönstret förstoras
      if (isMobile && processes.length > 3) {
        useCompactView = true;
      } 
      // Eller om vi är på en mellanstor skärm med många processer, visa bara några
      else if (isMobile && processes.length > 2) {
        // Hitta index för aktiv process
        const activeIndex = processes.findIndex(p => p.title === activeProcess);
        
        // Bestäm vilka processer som ska visas (aktiv + max 1 på varje sida)
        const startIdx = Math.max(0, activeIndex - 1);
        const endIdx = Math.min(processes.length, activeIndex + 2);
        visibleProcesses = processes.slice(startIdx, endIdx);
      }
      
      // Beräkna lämplig textstorlek baserat på skärmstorlek
      const baseFontSize = isMobile 
        ? (type === "main" ? 0.75 : 0.7) 
        : (type === "main" ? 0.875 : 0.75);
      
      // Beräkna genomsnittlig teckenbredd (approximativt)
      const charWidth = isMobile ? (type === "main" ? 7 : 6) : (type === "main" ? 8 : 7);
      
      // Basmått, justerade för mobil
      const numberSpace = isMobile ? 24 : 32; // Utrymme för nummer + marginaler
      const edgeSpace = isMobile ? 20 : 26;   // Utrymme för chevron-spetsarna
      const padding = isMobile ? 12 : 16;     // Intern padding
      
      // Om vi använder compact view, sätt bara dummy-bredder
      if (useCompactView) {
        setDimensions({
          widths: [containerWidth],
          fontSize: `${baseFontSize}rem`,
          isMobile,
          useCompactView,
          visibleProcesses
        });
        return;
      }
      
      // Utrymme för alla tecken totalt
      const totalChars = visibleProcesses.reduce((sum, p) => sum + (p.title?.length || 0), 0);
      const avgCharsPerProcess = totalChars / visibleProcesses.length;
      const totalMinWidth = visibleProcesses.length * (numberSpace + edgeSpace + padding) + (totalChars * charWidth);
      
      // Beräkna optimala fontstorlekar baserat på tillgängligt utrymme
      // Standardvärden (i rem-enheter)
      const minFontSize = isMobile ? 0.625 : 0.675; // Minimum font size
      
      // Beräkna skalningsfaktor för både bredd och fontstorlek
      let scaleFactor = 1;
      let fontSizeRem = baseFontSize;
      
      if (totalMinWidth > containerWidth) {
        // Vi behöver skala ner
        scaleFactor = containerWidth / totalMinWidth;
        
        // Skala fontstorlek baserat på tillgängligt utrymme och textstorlek
        if (scaleFactor < 0.9) {
          // Progressiv minskning av fontstorlek baserat på containerWidth och textlängd
          // För långa texter minskar vi fontstorleken mer aggressivt
          const fontScaleFactor = Math.max(scaleFactor, 0.7); // Begränsa skalning till inte mindre än 70%
          
          // Justera baserat på genomsnittlig textlängd
          const textLengthFactor = Math.min(1, 10 / avgCharsPerProcess);
          
          // Kombinera faktorerna för att få slutlig fontstorlek
          fontSizeRem = Math.max(
            minFontSize,
            baseFontSize * fontScaleFactor * (0.7 + (0.3 * textLengthFactor))
          );
          
          // Justera bredd-skalan för att kompensera för mindre text
          scaleFactor = Math.min(1, containerWidth / (visibleProcesses.length * (numberSpace + edgeSpace + padding) + (totalChars * charWidth * 0.85)));
        }
      }
      
      // Beräkna ideala bredder baserat på textlängd
      const idealWidths = visibleProcesses.map(process => {
        // På mobil behöver vi mindre minsta bredd
        const minWidth = isMobile ? 60 : 80; // Absolut minimum bredd
        const contentWidth = numberSpace + ((process.title?.length || 0) * charWidth) + padding;
        return Math.max(minWidth, contentWidth);
      });
      
      // Fördela tillgängligt utrymme relativt till idealbredderna
      const totalIdealWidth = idealWidths.reduce((sum, w) => sum + w, 0);
      
      let newWidths: number[];
      
      if (totalIdealWidth * scaleFactor <= containerWidth) {
        // Justera proportionellt men behåll relativa bredder
        const remainingSpace = containerWidth - (totalIdealWidth * scaleFactor);
        const extraPerItem = remainingSpace / visibleProcesses.length;
        
        newWidths = idealWidths.map(w => Math.floor((w * scaleFactor) + extraPerItem));
      } else {
        // Justera proportionellt utan extra utrymme
        newWidths = idealWidths.map(w => Math.floor(w * scaleFactor));
      }
      
      // Kontrollera att den totala bredden inte överstiger containerWidth
      const newTotalWidth = newWidths.reduce((sum, w) => sum + w, 0);
      
      if (newTotalWidth > containerWidth) {
        const excess = newTotalWidth - containerWidth;
        const reduction = excess / newWidths.length;
        newWidths = newWidths.map(w => Math.max(isMobile ? 50 : 60, w - reduction));
      }
      
      setDimensions({
        widths: newWidths,
        fontSize: `${fontSizeRem}rem`,
        isMobile,
        useCompactView,
        visibleProcesses
      });
    };
    
    // Beräkna initialt och omberäkna när fönstret ändrar storlek
    calculateLayout();
    
    const handleResize = () => {
      calculateLayout();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [processesKey, activeProcess, type, containerRef.current?.clientWidth]);
  
  // Om vi visar compact vy (select dropdown)
  if (dimensions.useCompactView) {
    return <MobileProcessSelector 
      processes={processes} 
      activeProcess={activeProcess} 
      onSelect={onSelect} 
      type={type}
      highlightedItems={highlightedItems}  
    />;
  }
  
  return (
    <div 
      ref={containerRef}
      className={cn(
        "flex w-full items-center process-navigation",
        type === "sub" ? "mt-4" : "pb-4"
      )}
      id={`process-navigation-${type}`}
      data-testid={`process-navigation-${type}`}
    >
      {/* Visa indikator om det finns processer till vänster som inte visas */}
      {dimensions.visibleProcesses.length < processes.length && 
       dimensions.visibleProcesses[0] !== processes[0] && (
        <button 
          onClick={() => {
            // Hitta föregående process (ett steg bakåt)
            const activeIndex = processes.findIndex(p => p.title === activeProcess);
            if (activeIndex > 0) {
              onSelect(processes[activeIndex - 1].title);
            }
          }}
          className="p-2 text-orange-300 hover:text-orange-400 focus:outline-none"
          id={`nav-prev-button-${type}`}
          aria-label="Föregående process"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </button>
      )}
      
      {/* Visa synliga processer */}
      <div id={`visible-processes-container-${type}`} className="flex items-center process-items-container">
        {dimensions.visibleProcesses.map((process, index) => {
          // Skapa ett säkert ID för processen
          const safeTitle = process.title ? process.title.replace(/\s+/g, '-').toLowerCase() : `unknown-${index}`;
          
          return (
            <div 
              key={process.title || `unknown-${index}`}
              className="flex items-center"
              style={{ marginRight: index < dimensions.visibleProcesses.length - 1 ? "-1px" : "0" }}
              id={`process-item-container-${type}-${safeTitle}`}
            >
              <ProcessChevron
                title={process.title || ""}
                number={process.number}
                active={activeProcess === process.title}
                onClick={() => onSelect(process.title)}
                width={dimensions.widths[index] || (dimensions.isMobile ? 80 : 120)}
                type={type}
                fontSize={dimensions.fontSize}
                isMobile={dimensions.isMobile}
                isHighlighted={process.title ? highlightedItems.has(process.title) : false}
              />
            </div>
          );
        })}
      </div>
      
      {/* Visa indikator om det finns processer till höger som inte visas */}
      {dimensions.visibleProcesses.length < processes.length && 
       dimensions.visibleProcesses[dimensions.visibleProcesses.length - 1] !== processes[processes.length - 1] && (
        <button 
          onClick={() => {
            // Hitta nästa process (ett steg framåt)
            const activeIndex = processes.findIndex(p => p.title === activeProcess);
            if (activeIndex < processes.length - 1) {
              onSelect(processes[activeIndex + 1].title);
            }
          }}
          className="p-2 text-orange-300 hover:text-orange-400 focus:outline-none"
          id={`nav-next-button-${type}`}
          aria-label="Nästa process"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ProcessNavigation;